import React, { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import OnboardingLayout from "@components/onboarding/onboarding_layout";
import { ApplicationDescription } from "@components/job_post/application_description";
import { QuestionFields } from "@components/job_post/question_fields";
import DiscardProgressModal from "@components/job_post/discard_progress_modal";
import { useApplicationForm } from "@components/job_post/use_application_form";
import { PrimaryButton } from "@sprout/buttons";
import { Body, SectionHeader, SectionTitle } from "@sprout/typography";
import type { JobPost, SearchSpecificFields } from "@/types/jben/job_post";
import type { BoardConfiguration } from "@/types/jben/board_configuration";

import "./application.scss";

type Props = {
  jobPost: JobPost & SearchSpecificFields;
  boardConfiguration: BoardConfiguration;
  submitPath: string;
  educationApiUrl?: string;
  dreamJobAvailable?: boolean;
  paths: { back: string; complete: string };
};

const OnboardingApplication = ({
  jobPost,
  boardConfiguration,
  submitPath,
  educationApiUrl,
  dreamJobAvailable,
  paths,
}: Props) => {
  const { t } = useTranslation("onboarding", { keyPrefix: "application" });
  const dirtyRef = useRef<() => boolean>(() => false);
  const bypassGuardRef = useRef(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const form = useApplicationForm({
    jobPost,
    questions: jobPost.questions,
    submitPath,
    educationApiUrl,
    dreamJobAvailable,
    dirtyRef,
    fromOnboarding: true,
    onSuccess: () => {
      bypassGuardRef.current = true;
      router.visit(paths.complete);
    },
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current()) event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    return router.on("before", (event) => {
      if (event.detail.visit.method === "get" && !bypassGuardRef.current && dirtyRef.current()) {
        event.preventDefault();
        setPendingUrl(event.detail.visit.url.pathname);
        setShowDiscard(true);
      }
    });
  }, []);

  const metaLine = [jobPost.company_name, jobPost.locations?.[0]].filter(Boolean).join(" · ");

  return (
    <div className="application-onboarding">
      <header className="application-onboarding__header">
        <span className="application-onboarding__eyebrow">{t("eyebrow")}</span>
        <SectionHeader as="h1" serif large>
          {jobPost.title}
        </SectionHeader>
        <div className="application-onboarding__meta">
          {metaLine && <Body metadata>{metaLine}</Body>}
          <button
            type="button"
            className="link application-onboarding__desc-toggle"
            aria-expanded={showDescription}
            onClick={() => setShowDescription((prev) => !prev)}
          >
            {showDescription ? t("hideJobDescription") : t("viewJobDescription")}
          </button>
        </div>
      </header>

      {showDescription && (
        <section className="application-onboarding__card application-onboarding__desc-card">
          <div className="application-onboarding__desc-head">
            <SectionTitle>{t("jobDescription")}</SectionTitle>
          </div>
          <ApplicationDescription
            introduction={jobPost.introduction}
            content={jobPost.content}
            conclusion={jobPost.conclusion}
            payRanges={jobPost.pay_ranges}
          />
        </section>
      )}

      <section className="application-onboarding__card">
        <div className="jben-form">
          <form id={"application-form"} onSubmit={form.handleSubmit} className="application--form">
            <QuestionFields
              form={form}
              jobPost={jobPost}
              boardConfiguration={boardConfiguration}
              educationApiUrl={educationApiUrl}
            />
            {form.submitErrorMessage && (
              <p id="submit-error" className="helper-text helper-text--error" aria-live="polite">
                {form.submitErrorMessage}
              </p>
            )}
          </form>
        </div>
      </section>

      <div className="application-onboarding__actions">
        <PrimaryButton
          onClick={form.handleSubmit}
          loading={form.submitting}
          disabled={form.submitting}
        >
          {t("submit")}
        </PrimaryButton>
      </div>

      <DiscardProgressModal
        open={showDiscard}
        onOpenChange={setShowDiscard}
        onDiscard={() => {
          bypassGuardRef.current = true;
          setShowDiscard(false);
          if (pendingUrl) router.visit(pendingUrl);
        }}
      />
    </div>
  );
};

OnboardingApplication.layout = (page: React.ReactNode) => (
  <OnboardingLayout>{page}</OnboardingLayout>
);

export default OnboardingApplication;
